import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FolderOpen, Plus, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Folder {
  id: string;
  name: string;
}

interface MoveToFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  savedResourceId: string;
  currentFolderId: string | null;
  onSuccess?: () => void;
}

const MoveToFolderDialog: React.FC<MoveToFolderDialogProps> = ({
  open,
  onOpenChange,
  savedResourceId,
  currentFolderId,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const [newFolderName, setNewFolderName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const { data: folders, isLoading } = useQuery({
    queryKey: ["user-folders"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("user_folders")
        .select("id, name")
        .eq("user_id", user.id)
        .order("name", { ascending: true });

      if (error) throw error;
      return data as Folder[];
    },
    enabled: open,
  });

  const moveMutation = useMutation({
    mutationFn: async (folderId: string | null) => {
      const { error } = await supabase
        .from("user_saved_resources")
        .update({ folder_id: folderId })
        .eq("id", savedResourceId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-resources"] });
      queryClient.invalidateQueries({ queryKey: ["my-collection"] });
      toast.success("Resource moved successfully");
      onOpenChange(false);
      onSuccess?.();
    },
    onError: () => {
      toast.error("Failed to move resource");
    },
  });

  const createFolderMutation = useMutation({
    mutationFn: async (name: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("user_folders")
        .insert({
          name,
          user_id: user.id,
          organization_id: organization?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["user-folders"] });
      setNewFolderName("");
      setIsCreating(false);
      // Move to the newly created folder
      moveMutation.mutate(data.id);
    },
    onError: () => {
      toast.error("Failed to create folder");
    },
  });

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    createFolderMutation.mutate(newFolderName.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Move to Folder</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[300px] pr-4">
          <div className="space-y-1">
            {/* Uncategorized option */}
            <Button
              variant={currentFolderId === null ? "secondary" : "ghost"}
              className="w-full justify-start gap-2"
              onClick={() => moveMutation.mutate(null)}
              disabled={moveMutation.isPending}
            >
              <FolderOpen className="h-4 w-4 text-muted-foreground" />
              <span className="flex-1 text-left">Uncategorized</span>
              {currentFolderId === null && <Check className="h-4 w-4 text-primary" />}
            </Button>

            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              folders?.map((folder) => (
                <Button
                  key={folder.id}
                  variant={currentFolderId === folder.id ? "secondary" : "ghost"}
                  className="w-full justify-start gap-2"
                  onClick={() => moveMutation.mutate(folder.id)}
                  disabled={moveMutation.isPending}
                >
                  <FolderOpen className="h-4 w-4 text-primary" />
                  <span className="flex-1 text-left">{folder.name}</span>
                  {currentFolderId === folder.id && <Check className="h-4 w-4 text-primary" />}
                </Button>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Create new folder */}
        <div className="border-t pt-4 mt-2">
          {isCreating ? (
            <div className="flex gap-2">
              <Input
                placeholder="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
                autoFocus
              />
              <Button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim() || createFolderMutation.isPending}
                size="sm"
              >
                {createFolderMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Add"
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsCreating(false);
                  setNewFolderName("");
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setIsCreating(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Folder
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MoveToFolderDialog;